import {
  type ChangeEvent,
  type ReactNode,
  forwardRef,
  useEffect,
  useState,
} from "react"
import { Box, CircularProgress, FormHelperText, IconButton, Stack } from "@mui/material"
import { toast } from "react-toastify"
import { AddAPhotoRoundedIcon } from "../../../icons/AddAPhotoRoundedIcon"
import { IconCloseRed } from "../../../icons/IconCloseRed"
import {
  BoxImageIconStyle,
  BoxImgStyle,
  ContainerBoxStyle,
  IconButtonStyle,
  avatarPreloadOverlayStyle,
} from "./style"
import { ImageCrop } from "../ImageCrop"

interface AvatarUploadProps {
    value?: string | File | null
    onChange?: (value: string | File | null) => void
    onBlur?: () => void
    name?: string
    error?: boolean
    helperText?: ReactNode
    onCropSaved?: (file: File) => Promise<string | void>
    hideRemove?: boolean
}

export const AvatarUpload = forwardRef<HTMLInputElement, AvatarUploadProps>((props, ref) => {
  const { value, onChange, onBlur, name, error, helperText, onCropSaved, hideRemove = false } = props
  const [pendingFile, setPendingFile] = useState<File | null>(null)
  const [previewLoading, setPreviewLoading] = useState(false)
  const [showCropModal, setShowCropModal] = useState(false)
  const [cropObjectUrl, setCropObjectUrl] = useState<string | null>(null)
  const [localPreviewUrl, setLocalPreviewUrl] = useState<string | null>(null)
  const [isSavingCrop, setIsSavingCrop] = useState(false)

  const isDefault = !value

  useEffect(() => {
    if (!value) {
      setLocalPreviewUrl(null)
      return
    }

    if (typeof value === "string") {
      setLocalPreviewUrl(null)
      return
    }

    const url = URL.createObjectURL(value)
    setLocalPreviewUrl(url)

    return () => {
      URL.revokeObjectURL(url)
    }
  }, [value])

  useEffect(() => {
    if (!pendingFile) {
      setCropObjectUrl(null)
      setPreviewLoading(false)
      setShowCropModal(false)
      return
    }

    const url = URL.createObjectURL(pendingFile)
    setCropObjectUrl(url)
    setPreviewLoading(true)
    setShowCropModal(false)

    let cancelled = false;
    const img = new Image();
    img.onload = () => {
      if (cancelled) return
      setPreviewLoading(false)
      setShowCropModal(true)
    }
    img.onerror = () => {
      if (cancelled) return
      URL.revokeObjectURL(url)
      setCropObjectUrl(null)
      toast.error("Не удалось загрузить изображение")
      setPendingFile(null)
      setPreviewLoading(false)
      setShowCropModal(false)
    }
    img.src = url

    return () => {
      cancelled = true
      URL.revokeObjectURL(url)
    }
  }, [pendingFile])

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (files && files.length > 0) {
      const file = files[0]
      setPendingFile(file)
      setPreviewLoading(true)
      setShowCropModal(false)
    }
    event.target.value = ""
  }

  const resetCropFlow = () => {
    setPendingFile(null)
    setPreviewLoading(false)
    setShowCropModal(false)
  }

  const handleCropBlob = async (blob: Blob) => {
    if (!pendingFile) return
    const out = new File([blob], pendingFile.name, {
      type: blob.type || "image/jpeg",
      lastModified: Date.now(),
    })

    if (onCropSaved) {
      setIsSavingCrop(true)
      try {
        const url = await onCropSaved(out)
        const nextValue = typeof url === 'string' && url.trim().length > 0 ? url : out
        onChange?.(nextValue)
        resetCropFlow()
      } catch {
        toast.error("Не удалось сохранить изображение")
      } finally {
        setIsSavingCrop(false)
      }
      return
    }

    onChange?.(out)
    resetCropFlow()
  }

  const handleRemove = (e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    onChange?.(null)
  }

  const showPreloadOverlay =
    previewLoading && pendingFile != null && !showCropModal

    return (
      <Stack spacing={1} width="100%">
        {showCropModal && pendingFile && cropObjectUrl && (
          <ImageCrop
            imageSrc={cropObjectUrl}
            onCrop={(blob) => { void handleCropBlob(blob) }}
            onClose={resetCropFlow}
            isSaving={isSavingCrop}
          />
        )}
        <Box
          sx={{
            position: "relative",
            width: 120,
            height: 120,
            borderRadius: 1,
          }}
        >
          {showPreloadOverlay && (
            <Box
              sx={avatarPreloadOverlayStyle}
              aria-busy
              aria-live="polite"
            >
              <CircularProgress size={32} />
            </Box>
          )}
          <Box
            component="label"
            sx={{
              ...ContainerBoxStyle,
              cursor: "pointer",
              display: "flex",
              ...(showPreloadOverlay ? { pointerEvents: "none" } : {}),
            }}
          >
            <input
              hidden
              accept="image/*"
              type="file"
              ref={ref}
              name={name}
              onBlur={onBlur}
              onChange={handleFileChange}
              disabled={showPreloadOverlay}
            />
            {isDefault ? (
              <AddAPhotoRoundedIcon fontSize="large" />
            ) : (
              <Box sx={BoxImageIconStyle}>
                <Box
                  component="img"
                  src={
                    typeof value === "string"
                      ? value
                      : localPreviewUrl ?? undefined
                  }
                  sx={BoxImgStyle}
                  alt=""
                />
                {!hideRemove && (
                  <IconButton
                    onClick={handleRemove}
                    size="small"
                    sx={IconButtonStyle}
                  >
                    <IconCloseRed fontSize="small" />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        </Box>
        {helperText != null && helperText !== "" && (
          <FormHelperText error={Boolean(error)} sx={{ mx: 0 }}>
            {helperText}
          </FormHelperText>
        )}
      </Stack>
    )
  }
)

AvatarUpload.displayName = "AvatarUpload"
